import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared/Common";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { Pagination } from "@/components/shared/Pagination";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";

export default function Workforce() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [teams, setTeams] = useState([]);
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (team !== "all") q.set("team", team);
    if (dateRange?.from) q.set("date_from", dateRange.from);
    if (dateRange?.to) q.set("date_to", dateRange.to);
    q.set("limit", String(pageSize));
    q.set("skip", String((page - 1) * pageSize));
    const { data } = await api.get(`/employees?${q.toString()}`);
    setItems(data.items);
    setTotal(data.total || data.items.length);
  }, [search, team, dateRange, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, team, dateRange, pageSize]);
  useEffect(() => { api.get("/teams").then(r => setTeams(r.data.items)); }, []);

  return (
    <div>
      <PageHeader title="Workforce" subtitle={`${total} employees · ${teams.length} teams`}>
        <Button variant="outline" className="h-9" onClick={() => window.open(`${API}/reports/export?type=employees`, "_blank")}>
          <DownloadSimple size={16} className="mr-1" /> Export
        </Button>
      </PageHeader>

      <div className="border rounded-md bg-card">
        <div className="p-4 border-b flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="workforce-search-input" placeholder="Search by name…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" />
          </div>
          <Select value={team} onValueChange={setTeam}>
            <SelectTrigger data-testid="workforce-team-filter" className="h-9 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {teams.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DateRangePicker testId="workforce-date-range" value={dateRange} onChange={setDateRange} />
        </div>

        {items.length === 0 && <EmptyState title="No employees found" />}
        {items.length > 0 && (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Team</th>
                  <th className="px-4 py-3">Skills</th>
                  <th className="px-4 py-3">Shift</th>
                  <th className="px-4 py-3">Availability</th>
                  <th className="px-4 py-3">Performance</th>
                  <th className="px-4 py-3">Quality</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map(e => (
                  <tr key={e.id} data-testid={`workforce-row-${e.id}`} onClick={() => nav(`/workforce/${e.id}`)} className="hover:bg-secondary/40 cursor-pointer">
                    <td className="px-4 py-3 font-medium">{e.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{e.team}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {(e.skills || []).slice(0, 3).map(s => (
                          <span key={s.skill} className="text-[10px] bg-secondary rounded px-1.5 py-0.5 mono">{s.skill}·L{s.level}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">{e.shift}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${
                        e.availability === "available" ? "bg-emerald-100 text-emerald-800" :
                        e.availability === "busy" ? "bg-amber-100 text-amber-800" :
                        "bg-slate-200 text-slate-700"
                      }`}>{e.availability}</span>
                    </td>
                    <td className="px-4 py-3 mono">{e.performance_score}</td>
                    <td className="px-4 py-3 mono">{e.quality_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination testId="workforce-pagination" page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>
    </div>
  );
}
